
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Calendar, Newspaper } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Press = () => {
  const { t, i18n } = useTranslation(['press', 'translation']);

  // Press mentions - mix of real and mock items
  const pressItems = [
    {
      title: "Han guidar till ett liv utan techjättar: \"Det börjar spåra med vilken kontroll de har\"",
      publication: "Aftonbladet",
      date: "2025-06-22",
      excerpt: "Trumps maktövertagande har inneburit större kontroll av gemene man – och inte bara i USA. Det säger Jonatan Tensetti – som varnar för konsekvenserna av tech-jättarnas samröre med makten. \"Vill man missbruka det och så kan man göra det\", säger han.",
      url: "https://www.aftonbladet.se/nyheter/a/yEByea/guide-sa-doljer-du-dig-fran-techjattarna-steg-for-steg",
      type: "interview",
      real: true
    },
    {
      title: "Ta den skånska vägen bort från Silicon Valley – här är hela guiden",
      publication: "Sydsvenskan",
      date: "2025-06-21",
      excerpt: "Grundaren av Frikopplad har gjort en pedagogisk välgärning genom att dela upp våra viktigaste tjänster och hur man byter ut dem i olika paket.",
      url: "https://www.sydsvenskan.se/artikel/ta-den-skanska-vagen-bort-fran-silicon-valley-har-ar-hela-guiden/",
      type: "article",
      real: true
    },
    {
      title: "Här är sajten som visar hur du bryter med techjättarna",
      publication: "NyTeknik",
      date: "2025-06-01",
      excerpt: "Han är läraren som blev ai-verksamhetsutvecklare och piratpartist. Nu vill Jonatan Tensetti att du ska bryta med techjättarna – och har skapat en sajt som visar hur.",
      url: "https://www.nyteknik.se/tech/har-ar-sajten-som-visar-hur-du-bryter-med-techjattarna/4364317",
      type: "interview",
      real: true
    }
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return <ExternalLink className="h-4 w-4" />;
      default:
        return <Newspaper className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    return t(`types.${type}`, { defaultValue: type });
  };

  const getReadArticleText = () => {
    return i18n.language === 'sv' ? 'Läs hela artikeln' : t('readArticle');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={`${t('hero.title')} - Frikopplad`}
        description={t('hero.description')}
        url="/press"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-frikopplad-cream py-16">
          <div className="container-custom">
            <div className="max-w-3xl">
              <h1 className="text-4xl font-bold text-frikopplad-navy mb-4">
                {t('hero.title')}
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                {t('hero.description')}
              </p>
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <Newspaper className="h-4 w-4" />
                  <span>{pressItems.length} {t('hero.mentions')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Press Coverage */}
        <div className="container-custom py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {pressItems.map((item, index) => (
              <Card key={index} className={`shadow-sm hover:shadow-md transition-shadow ${item.real ? 'ring-2 ring-frikopplad-blue/20' : ''}`}>
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2 text-sm text-frikopplad-blue">
                      {getTypeIcon(item.type)}
                      <span className="font-medium">{getTypeLabel(item.type)}</span>
                      {item.real && <span className="bg-frikopplad-blue text-white text-xs px-2 py-1 rounded">{t('recent')}</span>}
                    </div>
                    <time className="text-sm text-gray-500">
                      {new Date(item.date).toLocaleDateString(i18n.language === 'sv' ? 'sv-SE' : 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </time>
                  </div>
                  <CardTitle className="text-xl leading-tight">
                    <a 
                      href={item.url}
                      className="hover:text-frikopplad-red transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.title}
                    </a>
                  </CardTitle>
                  <CardDescription className="text-frikopplad-navy font-medium">
                    {item.publication}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    {item.excerpt}
                  </p>
                  <a
                    href={item.url}
                    className="inline-flex items-center text-frikopplad-blue hover:text-frikopplad-red transition-colors font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getReadArticleText()}
                    <ExternalLink className="ml-1 h-4 w-4" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Press;
